<template>
  <div class="main-content wide">
    <div
      :class="['lad-page-lead-img', { 'fallback-enabled': fallbackImageVisible }]"
      :style="{ 'background-image': `${responsiveImage(heroImages)}` }"
    >
      <div
        v-if="pageTitle"
        class="wrap"
      >
        <h1 class="header-title">{{ pageTitle }}</h1>
        <search v-if="isDesktop" />
      </div>
    </div>

    <div class="wrap">
      <search
        v-if="!isDesktop"
        @scroll="scrollToBlock"
      />
      <template v-if="airport">
        <header
          ref="headerContainer"
          class="header-container"
        >
          <div class="block info">
            <h2 class="title">{{ airportLabel }}</h2>
            <a
              v-if="!isMobile && viewTimetable"
              :href="viewTimetable.href"
              :target="viewTimetable.target"
              class="link"
            >
              <span>{{ viewTimetable.text }}</span>
            </a>
          </div>
          <date-switcher
            class="block date-switcher"
            :dates="dates"
          />
        </header>

        <section
          v-if="flightList.length"
          class="flight-list"
        >
          <flight-item
            v-for="(flight, index) in flightList"
            :key="index"
            :flight="flight"
            :statuses="statuses"
            :status-mappings="statusMappings"
            :type="type"
            :airport="airport"
          />
        </section>

        <error-container
          v-if="isDesktop && airport && !flightList.length"
          :title="getTitle"
          :description="errors.description"
          :link="errors.link"
        />

        <section v-if="showServicesBlock">
          <services-container
            :title="servicesTitle"
            :services="services"
          />
        </section>
      </template>

      <error-container
        v-if="!isDesktop && airport && !flightList.length"
        :title="getTitle"
        :description="errors.description"
        :link="errors.link"
      />
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import moment from 'moment'
  import ScrollUtils from 'utils/scrollUtils'
  import OrigDestUtils from 'store/utils/eHorizonOrigDestUtils'

  export default {
    components: {
      Search: () => import(/* webpackChunkName: "lad-search" */ 'containers/Pages/LAD/Search/Layout'),
      FlightItem: () => import(/* webpackChunkName: "lad-flight" */ 'containers/Pages/LAD/FlightItem'),
      ErrorContainer: () => import(/* webpackChunkName: "lad-error" */ 'containers/Pages/LAD/ErrorContainer'),
      DateSwitcher: () => import(/* webpackChunkName: "lad-date-switcher" */ 'containers/Pages/LAD/DateSwitcher'),
      ServicesContainer: () => import(/* webpackChunkName: "lad-services" */ 'containers/Pages/LAD/ServicesContainer')
    },
    mixins: [
      ImageHelpers
    ],
    data () {
      return {
        fallbackImageVisible: false,
        dates: {
          yesterday: moment().subtract(1, 'days'),
          today: moment(),
          tomorrow: moment().add(1, 'days'),
        },
      }
    },
    computed: {
      ...mapGetters('device', [
        'isDesktop',
        'isMobile'
      ]),
      ...mapState('lad', [
        'pageTitle',
        'heroImages',
        'flightData',
        'statuses',
        'statusMappings',
        'viewTimetable',
        'servicesTitle',
        'services',
        'airport',
        'activeDate',
        'currentTab'
      ]),
      ...mapState('lad/search', [
        'type'
      ]),
      ...mapState('lad', [
        'errors'
      ]),
      airportLabel () {
        return OrigDestUtils.toLabel(this.airport)
      },
      getTitle() {
        let key = 'arrivalTitle'
        if (this.type === 'orig') {
          key = 'departureTitle'
        }

        if (this.currentTab === 'flight-number') {
          key = 'flightNumberTitle'
        }

        let title = this.errors[`${key}`];
        title = title.replace('[APT]', this.airport.apt)

        return title
      },
      flightList () {
        const dates = []
        Object.values(this.dates).map((date) => {
          dates.push(date.format('YYYY-MM-DD'));
        })
        const flights = []
        Object.keys(this.flightData).some(item => {
          if (dates.includes(item)) {
            let filtered = []
            if (this.type === 'orig') {
              filtered = this.filteredData(this.flightData[`${item}`], 'depart')
            }

            if (this.type === 'dest') {
              filtered = this.filteredData(this.flightData[`${item}`], 'arrival')
            }

            flights[`${item}`] = filtered
          }
        })

        return typeof flights[this.activeDate] !== 'undefined' ? flights[this.activeDate] : []
      },
      showServicesBlock () {
        const blocks = Object.keys(this.services).length

        return this.flightList.length && blocks
      }
    },
    methods: {
      scrollToBlock() {
        if (this.isMobile) {
          const container = this.$refs.headerContainer
          if (typeof container !== 'undefined') {
            const scroll = container.getBoundingClientRect().top + window.scrollY;
            ScrollUtils.smoothScroll((scroll - 66), 300)
          }
        }
      },
      filteredData (item, type) {
        return item.sort((a, b) => {
          const aCType = a.changes[`${type}`]
          const bCType = b.changes[`${type}`]
          const aType = a[`${type}`]
          const bType = b[`${type}`]

          if (aCType !== '' && bCType !== '') {
            return aCType.localeCompare(bCType)
          }

          if (aCType === '' && bCType === '') {
            return aType.localeCompare(bType)
          }
        })
      },
      responsiveImage(data) {
        const image = this.$_ImageHelpers_responsiveImage({
          desktop: data.desktopImg.source,
          tablet: data.tabletImg.source,
          mobile: data.mobileImg.source
        })

        if (typeof image === 'undefined') {
          this.fallbackImageVisible = true
          return ``
        }

        return `url(${image})`
      }
    }
  }

</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import "~assets/styles/common.scss";

  .main-content.wide {
    position: relative;
    background: $gray-light-background;
  }

  .lad-page-lead-img {
    font-family: $base-font-family;
    position: relative;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    height: 240px;
    padding: 27px 24px;

    @include bp-mf-tablet {
      padding-top: 48px;
      padding-bottom: 48px;
      height: 361px;
    }

    @include bp-mf-desktop {
      height: 420px;
    }

    &.fallback-enabled {
      background-image: url('~assets/images/index-page/fallback-image.jpg');
    }

    .header-title {
      color: $blue-dark;
      font-size: em(32);
      font-weight: 500;
      line-height: 38px;
      margin: 0;

      @include bp-mf-tablet {
        font-size: em(48);
        line-height: 57px;
      }
    }

    .wrap {
      padding: 0;
    }
  }

  .wrap {
    display: flex;
    flex-direction: column;
    color: $grey-default;
    font-weight: 400;
    justify-content: space-between;
    min-width: 272px;
    width: 100%;

    @include bp-mf-desktop {
      display: block;
    }
  }

  .header-container {
    display: flex;
    flex-direction: column;
    margin: 0 12px 12px;

    @include bp-mf-tablet {
      flex-direction: row;
      margin: 0 26px 28px;
    }

    @include bp-mf-desktop {
      margin: 40px 0 32px;
    }

    .block {
      width: 100%;

      &.date-switcher {

        @include bp-mf-tablet {
          max-width: 320px;
        }
      }

      &.info {
        margin-bottom: 18px;

        @include bp-mf-tablet {
          margin-bottom: 0;
          max-width: calc(100% - 320px);
        }
      }
    }

    .title {
      color: $blue-dark;
      font-size: em(20);
      font-weight: 500;
      line-height: 32px;
      margin: 0 0 12px 0;

      @include bp-mf-tablet {
        font-size: em(24);
        line-height: 28px;
      }

      @include bp-mf-desktop {
        margin-bottom: 15px;
      }
    }

    .link {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      color: $blue-medium;
      font-size: em(14);
      font-weight: 500;
      line-height: 24px;
      text-decoration: none;

      span {
        text-decoration: underline;
      }

      &:before {
        @include filled-icon-font();
        content: $filled-icon-clock;
        display: flex;
        align-items: center;
        width: 24px;
        height: 24px;
        font-size: em(24);
        color: #4C5974;
        margin-right: 14px;
      }
    }
  }

  .flight-list {
    margin: 0 12px 32px;

    @include bp-mf-tablet {
      margin: 0 26px 36px;
    }

    @include bp-mf-desktop {
      margin: 0 0 48px;
    }
  }
</style>
